import React from 'react'
import ReactDOM from 'react-dom'
import { I18nextProvider } from 'react-i18next'
import { Provider } from 'react-redux'

import SnackbarProvider from '@scaleflex/ui/snackbar'
import ThemeProvider from '@scaleflex/ui/theme'
import SharedAdminKit, { CoreProvider } from '@shared-admin-kit/core'
import { TranslationModule } from '@shared-admin-kit/translation'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ConnectedRouter } from 'connected-react-router'
import { createBrowserHistory } from 'history'

import App from './app'
import MainLoader from './components/main-loader'
// import reportWebVitals from './reportWebVitals';
import './index.css'
import { GlobalNotificationsProvider } from './providers/global-notifications-provider'
import { AUTHENTICATED_ROUTES, NOT_AUTHENTICATED_ROUTES, ROUTE_PATHS } from './routes/routes.constants'
import { client } from './services/api/client'
import i18nInstance from './services/i18n.client'
import { createStore } from './store'
import theme from './theme'

const {
  REACT_APP_AUTH_PUBLIC_URL,
  REACT_APP_DEV_LOGIN_ENABLED,
  REACT_APP_I18N_IT_BASE_URL,
  REACT_APP_I18N_GRID_UUID,
  REACT_APP_DEBUG_LANGUAGE,
} = process.env

const history = createBrowserHistory()

const core = SharedAdminKit({
  client,
  history,
  authRoutes: Object.keys(AUTHENTICATED_ROUTES).map((routeKey) => AUTHENTICATED_ROUTES[routeKey]),
  publicRoutes: Object.keys(NOT_AUTHENTICATED_ROUTES).map((routeKey) => NOT_AUTHENTICATED_ROUTES[routeKey]),
  devLogin: { enabled: REACT_APP_DEV_LOGIN_ENABLED === 'true' },
  loginUrl: REACT_APP_AUTH_PUBLIC_URL,
  sessionChecker: { enabled: false, redirectSessionExpired: ROUTE_PATHS.SESSION_EXPIRED },
}).use(TranslationModule, {
  i18nInstance,
  i18nBaseUrl: REACT_APP_I18N_IT_BASE_URL,
  i18nGridUuid: REACT_APP_I18N_GRID_UUID,
  debug: REACT_APP_DEBUG_LANGUAGE === 'true',
})

const store = createStore(core.getAllReducers(), history)

const handleForbiddenPage = () => {
  if (['/', AUTHENTICATED_ROUTES.GRIDS].includes(window.location.pathname)) {
    window.location.href = process.env.REACT_APP_AUTH_PUBLIC_URL
  }

  return '' // '403 Forbidden';
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // Don't refetch data on window focus
      refetchOnReconnect: false,
      refetchOnMount: false,
      retry: 1, // Will retry failed requests 1 time before displaying an error
      staleTime: Infinity, // Data will never be considered stale
      cacheTime: Infinity, // Data will never be removed from cache
    },
  },
})

ReactDOM.render(
  <React.StrictMode>
    <I18nextProvider i18n={i18nInstance}>
      <Provider store={store}>
        <ConnectedRouter history={history}>
          <ThemeProvider theme={theme}>
            <SnackbarProvider anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
              <CoreProvider core={core} loader={<MainLoader />} onForbiddenPage={handleForbiddenPage}>
                <GlobalNotificationsProvider>
                  <QueryClientProvider client={queryClient}>
                    <App />
                  </QueryClientProvider>
                </GlobalNotificationsProvider>
              </CoreProvider>
            </SnackbarProvider>
          </ThemeProvider>
        </ConnectedRouter>
      </Provider>
    </I18nextProvider>
  </React.StrictMode>,
  document.getElementById('root'),
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();
